import {
  useApiCreate,
  useApiList,
  useApiSingle,
  useApiUpdate,
  useFirstFromList,
} from '@/composables/api'
import { Post } from '@/model/data-object/Post'
import { NewPost } from '@/model/data-object/NewPost'
import useConfiguration from '@/composables/configuration/configuration'
import { AxiosRequestConfig } from 'axios'
import { ORDER } from '@/composables/api'
import { ApiTimeout } from '@/composables/apiTimeout'
import { VoteStatsResponse } from '@/model/data-object/VoteStatsResponse'
import { PostUpdate } from '@/model/data-object/PostUpdate'
import { VoteRes } from '@/model/data-object/VoteRes'
import { SuperLikeRes } from '@/model/data-object/SuperLikeRes'

async function create(post: NewPost): Promise<Post> {
  const { request, response, error } = useApiCreate<Post>(
    '/api/prv/v1/post',
    post,
    {}
  )
  await request()

  if (typeof error.value !== 'undefined') {
    throw error.value
  }

  return response.value as Post
}

function postSuperLike(postId: number) {
  return useApiCreate<SuperLikeRes>('/api/prv/v1/superLike/' + postId, {})
}

function update(post: PostUpdate) {
  return useApiUpdate<Post>('/api/prv/v1/post/' + post.id, post, {})
}

function useApiListWrap(
  url: string,
  postId?: number,
  options?: AxiosRequestConfig,
  apiTimeout?: ApiTimeout
) {
  if (postId !== undefined) {
    url += '/post/' + postId
  }
  return useApiList<Array<Post>>(url, options, apiTimeout)
}

function fetchMyHidden(postId?: number, apiTimeout?: ApiTimeout) {
  const { configuration } = useConfiguration()

  return useApiListWrap(
    '/api/prv/v1/post/hidden/current-user',
    postId,
    {
      params: {
        limit: configuration.pageSize,
        order: ORDER.DESC,
      },
    },
    apiTimeout
  )
}

function fetchSingle(postId: number) {
  return useFirstFromList<Post | null>('/api/pub/v1/post/ids/' + postId)
}

function fetchSingleWithRawContent(postId: number) {
  return useApiSingle<Post | null>('/api/prv/v1/post/' + postId)
}

function fetchBest(pageSize: number, apiTimeout?: ApiTimeout) {
  const { configuration } = useConfiguration()

  return useApiList<Array<Post>>(
    '/api/pub/v1/post/best/topic/' + configuration.widget.topicId,
    {
      params: {
        limit: pageSize,
      },
    },
    apiTimeout
  )
}

function fetchPostStats(ids: number[]) {
  return useApiList<Array<VoteStatsResponse>>(
    '/api/pub/v1/post/stats/ids/' + ids.join(',')
  )
}

function fetchNewest(
  orderBy: ORDER,
  postId?: number,
  apiTimeout?: ApiTimeout,
  limit?: undefined | number
) {
  const { configuration } = useConfiguration()

  return useApiListWrap(
    'api/pub/v1/post/treedesc/topic/' + configuration.widget.topicId,
    postId,
    {
      params: {
        limit: limit ? limit : configuration.pageSize,
        order: orderBy,
      },
    },
    apiTimeout
  )
}

function fetchOldest(
  orderBy: ORDER,
  postId?: number,
  apiTimeout?: ApiTimeout,
  limit?: undefined | number
) {
  const { configuration } = useConfiguration()

  return useApiListWrap(
    'api/pub/v1/post/treeasc/topic/' + configuration.widget.topicId,
    postId,
    {
      params: {
        limit: limit ? limit : configuration.pageSize,
        order: orderBy,
      },
    },
    apiTimeout
  )
}

function fetchMyVisible(postId?: number, apiTimeout?: ApiTimeout) {
  const { configuration } = useConfiguration()

  return useApiListWrap(
    '/api/prv/v1/post/visible/current-user',
    postId,
    {
      params: {
        limit: configuration.pageSize,
        order: ORDER.DESC,
      },
    },
    apiTimeout
  )
}

function fetchByAuthor(postId?: number, apiTimeout?: ApiTimeout) {
  const { configuration } = useConfiguration()

  return useApiListWrap(
    '/api/pub/v1/post/visible/author/' + configuration.widget.userId,
    postId,
    {
      params: {
        limit: configuration.pageSize,
        order: ORDER.DESC,
      },
    },
    apiTimeout
  )
}

function fetchTopLevelNewest(pageSize: number) {
  const { configuration } = useConfiguration()

  return useApiList<Array<Post>>(
    '/api/pub/v1/post/newest/topic/' + configuration.widget.topicId,
    {
      params: { limit: pageSize },
    }
  )
}

function fetchByIds(ids: string[]) {
  return useApiList<Post[]>('api/pub/v1/post/ids/' + ids.join(','), {})
}

function fetchPath(id: number) {
  return useApiList<Post[]>('api/pub/v1/post/path/' + id, {})
}

function fetchMyUnseenReplies(apiTimeout?: ApiTimeout) {
  const { configuration } = useConfiguration()

  return useApiList<Array<Post>>(
    '/api/prv/v1/post/replies-unseen/current-user',
    {
      params: {
        limit: configuration.pageSize,
      },
    },
    apiTimeout
  )
}

function fetchMyAllReplies(postId?: number, apiTimeout?: ApiTimeout) {
  const { configuration } = useConfiguration()

  return useApiListWrap(
    '/api/prv/v1/post/replies/current-user',
    postId,
    {
      params: {
        limit: configuration.pageSize,
        order: ORDER.DESC,
      },
    },
    apiTimeout
  )
}

export default function usePostApi() {
  return {
    create,
    update,
    postSuperLike,
    fetchByIds,
    fetchPath,
    fetchMyHidden,
    fetchMyVisible,
    fetchBest,
    fetchNewest,
    fetchOldest,
    fetchSingle,
    fetchTopLevelNewest,
    fetchByAuthor,
    fetchMyUnseenReplies,
    fetchMyAllReplies,
    fetchPostStats,
    fetchSingleWithRawContent,
  }
}
